import {Selector} from "../mixins/dictionaries";
import {paddingOligoEditor as paddingEditor} from "../mixins/constants.js"
import {getRangeBases, toolTipContent, textSize, getContrastColor, fixedMaxValue} from "./utils.js";

import {line} from "d3-shape";
import {drag} from "d3-drag";
import {select, selectAll} from "d3-selection";

const d3 = {select, selectAll, drag, line}

import {
    editorMixins,
    brushMixins,
} from "../mixins/oligoMixins.js";

import {dnaClassColor, isInRange, isInsideArea, scaleAnnotationSize} from "../editor/utils";
import {annotationPath} from "../mixins/linearShape";

export default class CartoonEditor {
    constructor(settings, mediator) {
        this.settings = {
            ...settings
        }
        Object.assign(
            this,
            editorMixins,
            brushMixins
        );

        this.mediator = mediator;
        this.initDataContainer();
        this.initEditor(Selector.CARTOON_EDITOR);
    }

    initDataContainer() {
        this.$activeContainer = document.querySelector("#editor .tab-pane.active");
        this.fixedFontSize = 2.5;

        this.offsetScroll = {
            "current": 0,
            "step": 0,
        }

        this.boundaries = {
            "left": 0,
            "right": 0,
        }

        this.regularSizeFont = textSize("ACGTACGTACGT", this.$activeContainer, this.fixedFontSize);
        const [fontWidth, fontHeight] = [this.regularSizeFont.width, this.regularSizeFont.height];
        this.fontBoxWidth = fontWidth / 12;
        this.fontSizeBase = fontWidth / 12;
        this.sourceObject = "cartoon";
        this.slots = [];
        this.slotFriendlyId = null;
    }

    initData(decodedStructuredData, sourceData) {
        this.characters = decodedStructuredData["decodedCartoonMap"];
        this.onlyCharacters = this.characters.filter(item => !item.bond);

        this.crossMap = decodedStructuredData["decodedCrossMap"];

        this.sequenceLength = this.characters.length;

        this.slots = sourceData.slots;

        // set init friendlyID
        if (!this.slotFriendlyId) {
            this.slotFriendlyId = sourceData.friendly_id;
            for (let i = 0; i < this.slots.length; i++) {
                if (this.slots[i].length) {
                    this.slotFriendlyId = this.slots[i].dna_sequence.friendly_id;
                    break;
                }
            }
        }
    }

    createTicks() {
        let position;

        this.rulerContainer.selectAll(".tickGroup").remove();

        /**
         * draw first tick if inside boundaries range
         */
        const firstBase = this.onlyCharacters[0];
        if (firstBase.charIndex >= this.boundaries.left && firstBase.charIndex < this.boundaries.right) {
            position = firstBase.charIndex
            this.renderTick(1, position);
        }

        // intermediate Ticks (multiples of 5, except 0)
        for (let index = 4; index < this.onlyCharacters.length; index += 5) {
            const base = this.onlyCharacters[index];
            if (base.cartoonStart > this.boundaries.left && base.cartoonStart < this.boundaries.right) {
                position = base.cartoonStart + paddingEditor
                this.renderTick(index + 1, position - this.offsetScroll.current);
            }
        }
    }

    renderTick(tickNumber, tickPosition) {
        const tickContainer = this.rulerContainer.append("g")
            .attr("class", "tickGroup")
            .attr("transform", "translate(" + ((tickPosition * this.fontBoxWidth) + (this.fontBoxWidth / 2)) + ",0)")

        tickContainer.append("line")
            .attr("class", "tickLine")
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", 0)
            .attr("y2", 10)
            .attr("stroke", "#000")
            .attr("stroke-width", 1)
            .attr("opacity", 1);

        tickContainer.append("text")
            .attr("class", "tickText font-monospace")
            .attr("dy", 20)
            .style("font-size", .7 + "em")
            .style('text-anchor', 'middle')
            .text(tickNumber)
    }

    renderSequence() {
        const self = this;
        const bases = getRangeBases(this.boundaries.left, this.boundaries.right, this.characters)
        this.sequenceContainer.selectAll("g.base").remove();

        this.sequenceContainer
            .attr("transform", `translate(${this.fontBoxWidth}, ${this.placements.editor.sequence})`)
            .selectAll("g.base")
            .data(bases)
            .enter()
            .append("g")
            .attr("transform", (d, index) => `translate(${index * self.fontBoxWidth + self.fontBoxWidth / 2}, 0)`)
            .each(function (d, index) {
                const group = d3.select(this);

                const sgroup = group.append("g")
                    .attr("class", "base")
                    .attr("data-tg-id", `encoding-id-${ index }`)
                    .style("cursor", "pointer")
                    .attr("transform", "translate(0, -5.3)")
                    .attr("data-toggle", "virtual-popper")
                    .attr("data-virtual-popper-html", "true")
                    .attr("data-virtual-popper-content", toolTipContent(d))
                    .on("contextmenu", event => {
                        event.preventDefault();
                        event.stopPropagation();

                        // mousedown stopPropagation prevents mainBox contextmenu from firing
                        self.setContexMenuEditor(event, "#contextMenuEditor")
                    })
                    .on("mousedown", event => {
                        // block parent click
                        event.stopPropagation();
                    })
                    .on("mouseup", event => {
                        event.preventDefault();
                        event.stopPropagation();

                        self.rangeBp = {
                            start: index + paddingEditor + self.offsetScroll.current,
                            end: index + 1 + paddingEditor + self.offsetScroll.current
                        }

                        self.renderBrush(self.rangeBp.start, self.rangeBp.end, self.offsetScroll.current, self.fontBoxWidth);

                        self.EventsTrigger("setOffsetScroll", {
                            "start": self.rangeBp.start,
                            "end": self.rangeBp.end,
                            "source": self.sourceObject
                        })

                        self.EventsTrigger("slide", "end");
                        self.EventsTrigger("updateRender");

                        self.EventsTrigger("selectRange", {
                            start: self.rangeBp.start,
                            end: self.rangeBp.end,
                            source: self.sourceObject
                        });

                    })

                sgroup.append("circle")
                    .attr("class", `cartoon-shape shape-circle ${d.shape === "circle" ? "" : "d-none"}`)
                    .attr("r", self.fontBoxWidth / 2)
                    .attr("cx", 0)
                    .attr("cy", -0.4)
                    .style("fill", d.color)
                    .style("stroke", "#fff")
                    .style("stroke-width", 0)
                    .style("opacity", .8)

                sgroup.append("path")
                    .attr("class", `cartoon-shape shape-rhombus ${d.shape === "rhombus" ? "" : "d-none"}`)
                    .attr("transform", `translate(-${self.fontBoxWidth / 2}, 0)`)
                    .attr("d", `M0,0L${self.fontBoxWidth / 2},-${self.fontBoxWidth / 2}L${self.fontBoxWidth},0l-${self.fontBoxWidth / 2},${self.fontBoxWidth / 2}z`)
                    .style("fill", d.color)
                    .style("stroke", "#fff")
                    .style("stroke-width", 0)
                    .style("opacity", .8)

                sgroup.append("path")
                    .attr("class", `cartoon-shape shape-rectangle ${d.shape === "rectangle" ? "" : "d-none"}`)
                    .attr("transform", `translate(-${self.fontBoxWidth / 2}, 0)`)
                    .attr("d", `M0,0L0,-${self.fontBoxWidth / 8}l${self.fontBoxWidth},0l0,${self.fontBoxWidth / 4}l-${self.fontBoxWidth},0z`)
                    .style("fill", d.color)
                    .style("stroke", "#fff")
                    .style("stroke-width", 0)
                    .style("opacity", .8)

                sgroup.append("text")
                    .attr("class", "base-text")
                    .attr("x", 0)
                    .attr("y", -.5)
                    .attr("text-anchor", "middle")
                    .attr("dominant-baseline", "central")
                    .style("font-size", ".6em")
                    .style("fill", getContrastColor(d.color))
                    .text(d.typeMap.symbol)
            });
    }

    updateBases() {
        const residues = getRangeBases(this.boundaries.left, this.boundaries.right, this.characters)
        const residueNodes = this.sequenceContainer.selectAll(".base").nodes();
        let residue;

        residueNodes.forEach((n, index) => {
                residue = residues[index];
                const shapes = n.querySelectorAll(`.cartoon-shape`);
                shapes.forEach(s => {
                    if (s.classList.contains(`shape-${residue.shape}`)) {
                        s.style.fill = residue.color
                        s.classList.remove('d-none')
                    } else {
                        s.classList.add('d-none')
                    }
                })

                const textEl = n.querySelector("text");
                textEl.innerHTML = residue.base;
                textEl.style.fill = getContrastColor((residue.color));

                n.setAttribute("data-virtual-popper-content", toolTipContent(residue))
            }
        )
    }

    renderSlots() {
        const slotHeight = 20;
        let start = 0;
        let end = 0;
        let cartoonStart = 0;
        let cartoonEnd = 0;

        this.slotsContainer
            .attr("transform", `translate(${-(this.offsetScroll.current * this.fontBoxWidth)}, ${this.placements.slots})`)
            .selectAll(".slot-group").remove();

        this.slots.forEach(s => {
            if (s.dna_sequence.encodings.length) {
                const slotLength = s.dna_sequence.encodings.length;
                cartoonStart = this.characters[start].cartoonStart;
                end = start + slotLength;
                cartoonEnd = end === this.sequenceLength ? this.sequenceLength : this.characters[end].cartoonStart;
                const downStreamLinkageLength = s.downstream_linkage ? -1 : 0;

                if (slotLength && isInsideArea(cartoonStart + paddingEditor, cartoonEnd + paddingEditor, this.boundaries.left, this.boundaries.right, this.sequenceLength)) {
                    const label = s.dna_sequence.name + " (" + s.dna_sequence.friendly_id + ")";
                    const container = this.slotsContainer
                        .append("g")
                        .attr("class", "slot-group slot-" + s.pk)
                        .attr('data-toggle', 'virtual-popper')
                        .attr("data-virtual-popper-content", label)
                        .attr("transform", `translate(${(cartoonStart + paddingEditor) * this.fontBoxWidth},0)`)

                    this.drawSlot(container, s, slotLength + downStreamLinkageLength, s.direction, dnaClassColor(s.dna_sequence), slotHeight);
                }
                start = end;
            }
        })
    }


    alignRangeBp(brushSize, event) {
        let start = brushSize.x / this.fontBoxWidth;
        let end = start + (brushSize.width / this.fontBoxWidth);

        const startAbs = fixedMaxValue(start + this.offsetScroll.current, paddingEditor, this.sequenceLength + paddingEditor);
        const endAbs = fixedMaxValue(end + this.offsetScroll.current, paddingEditor, this.sequenceLength + paddingEditor);

        if (startAbs === endAbs) {
            // send raw float position to resolve the friendlyId of the slot
            this.EventsTrigger("setCurrentEncodingAnchor", {
                "currentPosition": start - paddingEditor + this.offsetScroll.current,
                "source": this.sourceObject,
            });
        }

        this.rangeBp = { start: startAbs, end: endAbs }

        this.renderBrush(this.rangeBp.start, this.rangeBp.end, this.offsetScroll.current, this.fontBoxWidth);
    }

    scrollBrush() {
        const newBrushSize = {
            x: (this.rangeBp.start + this.offsetScroll.step) * this.fontBoxWidth,
            width: (this.rangeBp.end - this.rangeBp.start) * this.fontBoxWidth,
        }

        this.brush.setParameters(newBrushSize);
    }
}